import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  collection,
  doc,
  increment,
  onSnapshot,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { arc, hierarchy, partition, zoom as d3Zoom, zoomIdentity } from "d3";
import { select } from "d3-selection";

import { auth, db } from "../../firebase/firebase";
import { updateActivity } from "../dashboard/api/activity";

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const normalizePoints = (value) => {
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? 0 : numeric;
};

const createColorScale = (minPoints, maxPoints, mode) => {
  const min = Number.isFinite(minPoints) ? minPoints : 0;
  const max = Number.isFinite(maxPoints) && maxPoints > min ? maxPoints : min + 1;
  const saturation = mode === "dark" ? 62 : 70;
  const lightnessStart = mode === "dark" ? 36 : 58;
  const lightnessEnd = mode === "dark" ? 48 : 46;

  return (value) => {
    const ratio = clamp((value - min) / (max - min), 0, 1);
    const hue = 0 + ratio * 120; // red (0) to green (120)
    const lightness = lightnessStart + (lightnessEnd - lightnessStart) * ratio;
    return `hsl(${Math.round(hue)}, ${saturation}%, ${Math.round(lightness)}%)`;
  };
};

const buildHierarchy = (items) => {
  if (!items.length) return null;

  const nodeMap = new Map();
  items.forEach((item) => {
    nodeMap.set(item.title, { ...item, children: [] });
  });

  const roots = [];
  nodeMap.forEach((node) => {
    if (node.parent && nodeMap.has(node.parent)) {
      nodeMap.get(node.parent).children.push(node);
    } else {
      roots.push(node);
    }
  });

  if (roots.length === 1) {
    return roots[0];
  }

  return {
    title: "Bird's-Eye",
    id: "birds-eye-root",
    points: roots.reduce((total, root) => total + normalizePoints(root.points), 0),
    children: roots,
  };
};

const getTextColorForBackground = (hslString) => {
  const match = hslString.match(/hsl\((\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)%\s*,\s*(\d+(?:\.\d+)?)%\)/i);
  if (!match) {
    return "#0f172a";
  }
  const lightness = parseFloat(match[3]);
  return lightness < 45 ? "rgba(255,255,255,0.92)" : "#0f172a";
};

const SUNBURST_MARGIN = 32;
const SUNBURST_MIN_RADIUS = 80;
const MIN_LABEL_ARC_SPAN = 0.015;
const MIN_LABEL_RADIAL_SPAN = 10;

const MIN_CHILD_WEIGHT = 1;
const BASE_RING_FONT_SIZE = 6.5;
const LEAF_FONT_SCALE = 0.5;
const MIN_FONT_SIZE = 1.2;
const ZOOM_MIN_SCALE = 0.05;
const ZOOM_MAX_SCALE = Number.POSITIVE_INFINITY;

const getFontSizeForDepth = (depth) => {
  if (depth <= 1) return BASE_RING_FONT_SIZE;
  const scaled = BASE_RING_FONT_SIZE * Math.pow(LEAF_FONT_SCALE, depth - 1);
  return Math.max(MIN_FONT_SIZE, Number(scaled.toFixed(2)));
};

const getImmediateChildWeight = (node) => {
  if (!node || !node.children || node.children.length === 0) {
    return MIN_CHILD_WEIGHT;
  }
  return Math.max(node.children.length, MIN_CHILD_WEIGHT);
};

const redistributeAnglesByChildren = (node) => {
  if (!node || !node.children || node.children.length === 0) {
    return;
  }

  const start = Number.isFinite(node.x0) ? node.x0 : 0;
  const end = Number.isFinite(node.x1) ? node.x1 : start;
  const span = Math.max(end - start, 0);

  if (span <= 0) {
    node.children.forEach(redistributeAnglesByChildren);
    return;
  }

  const weights = node.children.map((child) => getImmediateChildWeight(child));
  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0) || node.children.length;

  let current = start;
  node.children.forEach((child, index) => {
    const weight = weights[index];
    const portion = span * (weight / totalWeight);
    const next = index === node.children.length - 1 ? end : current + portion;
    child.x0 = current;
    child.x1 = next;
    current = next;
    redistributeAnglesByChildren(child);
  });
};

const BirdsEyePage = () => {
  const theme = useTheme();
  const supportsHover = useMediaQuery("(hover: hover) and (pointer: fine)");

  const accentTone = useMemo(
    () =>
      theme.palette.secondary?.main ??
      theme.palette.primary.light ??
      theme.palette.primary.main,
    [theme]
  );

  const pageBackground = useMemo(() => {
    const base =
      theme.palette.mode === "light"
        ? theme.palette.grey[100]
        : theme.palette.background.default;
    const primaryGlow = alpha(
      theme.palette.primary.main,
      theme.palette.mode === "light" ? 0.12 : 0.36
    );
    const accentGlow = alpha(
      accentTone,
      theme.palette.mode === "light" ? 0.1 : 0.28
    );
    return `radial-gradient(circle at 0% -20%, ${primaryGlow} 0%, transparent 55%), radial-gradient(circle at 120% -15%, ${accentGlow} 0%, transparent 52%), ${base}`;
  }, [accentTone, theme]);

  const panelSurface = useMemo(
    () => ({
      position: "relative",
      overflow: "hidden",
      borderRadius: 3,
      background:
        theme.palette.mode === "light"
          ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(accentTone, 0.09)} 120%)`
          : `linear-gradient(135deg, ${alpha(theme.palette.primary.dark ?? theme.palette.primary.main, 0.55)} 0%, ${alpha(accentTone, 0.33)} 120%)`,
      border: `1px solid ${alpha(
        theme.palette.primary.main,
        theme.palette.mode === "light" ? 0.16 : 0.42
      )}`,
      boxShadow: theme.shadows[6],
      backdropFilter: "blur(18px)",
    }),
    [accentTone, theme]
  );

  const [userId, setUserId] = useState(null);
  const [progressItems, setProgressItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [pendingNodeIds, setPendingNodeIds] = useState(new Set());
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);

  const containerRef = useRef(null);
  const transformRef = useRef(zoomIdentity);
  const zoomBehaviorRef = useRef(null);
  const layoutInitializedRef = useRef(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUserId(user ? user.uid : null);
    });
    return () => unsubscribe();
  }, []);

  const [viewportSize, setViewportSize] = useState({ width: 960, height: 560 });
  const isLandscapeLayout = useMemo(
    () => viewportSize.width > viewportSize.height && viewportSize.width >= 560,
    [viewportSize.height, viewportSize.width]
  );

  useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return undefined;
    }

    const updateSize = () => {
      const { width, height } = element.getBoundingClientRect();
      setViewportSize({ width, height: Math.max(height, width * 0.58) });
    };

    updateSize();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateSize);
      observer.observe(element);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  useEffect(() => {
    if (!userId) {
      setProgressItems([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const q = query(collection(db, "progress"), where("uid", "==", userId));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const items = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            parent: data.parent || null,
            points: normalizePoints(data.points),
          };
        });
        setProgressItems(items);
        setLoading(false);
        setErrorMessage("");
      },
      (error) => {
        console.error("BirdsEyePage: failed to load progress", error);
        setErrorMessage("Unable to load your progress right now. Please try again later.");
        setProgressItems([]);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  const hierarchyData = useMemo(() => buildHierarchy(progressItems), [progressItems]);

  const sunburstLayout = useMemo(() => {
    if (!hierarchyData || !viewportSize.width || !viewportSize.height) {
      return { root: null, radius: 0 };
    }

    const minDimension = Math.min(viewportSize.width, viewportSize.height);
    const computedRadius = Math.max(minDimension / 2 - SUNBURST_MARGIN, SUNBURST_MIN_RADIUS);

    if (!Number.isFinite(computedRadius) || computedRadius <= 0) {
      return { root: null, radius: 0 };
    }

    const root = hierarchy(hierarchyData)
      .sum(() => 1)
      .sort((a, b) => (b.value || 0) - (a.value || 0));

    partition().size([2 * Math.PI, computedRadius]).padding(0.002)(root);
    redistributeAnglesByChildren(root);
    return { root, radius: computedRadius };
  }, [hierarchyData, viewportSize]);

  const sunburstRoot = sunburstLayout.root;

  const fallbackColorScale = useMemo(
    () => createColorScale(0, 1, theme.palette.mode),
    [theme.palette.mode]
  );

  const siblingColorScales = useMemo(() => {
    const map = new Map();
    if (!sunburstRoot) {
      return map;
    }

    sunburstRoot.each((node) => {
      if (!node.children || node.children.length === 0) {
        return;
      }

      const maxPoints = node.children.reduce(
        (max, child) => Math.max(max, normalizePoints(child.data.points)),
        0
      );

      const effectiveMax = Math.max(maxPoints, 1);
      map.set(node, createColorScale(0, effectiveMax, theme.palette.mode));
    });

    return map;
  }, [sunburstRoot, theme.palette.mode]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return undefined;
    }

    const zoomBehavior = d3Zoom()
      .scaleExtent([ZOOM_MIN_SCALE, ZOOM_MAX_SCALE])
      .filter((event) => {
        const tagName = event?.target?.tagName?.toLowerCase() || "";
        if (["button", "input", "textarea", "select", "a"].includes(tagName)) {
          return false;
        }
        if (event.type === "wheel") {
          return event.ctrlKey || event.metaKey || event.deltaMode === 0;
        }
        return true;
      })
      .wheelDelta((event) => -event.deltaY * 0.002)
      .on("start", (event) => {
        if (event?.sourceEvent?.type !== "wheel") {
          setIsPanning(true);
        }
      })
      .on("zoom", (event) => {
        transformRef.current = event.transform;
        setZoom(event.transform.k);
        setOffset({ x: event.transform.x, y: event.transform.y });
      })
      .on("end", () => {
        setIsPanning(false);
      });

    zoomBehaviorRef.current = zoomBehavior;

    const selection = select(element);
    selection.call(zoomBehavior).call(zoomBehavior.transform, transformRef.current);

    return () => {
      selection.on(".zoom", null);
    };
  }, []);

  useEffect(() => {
    if (supportsHover) return undefined;
    const handlePointerDown = (event) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target)) {
        setSelectedNodeId(null);
      }
    };
    window.addEventListener("pointerdown", handlePointerDown, { capture: true });
    return () => window.removeEventListener("pointerdown", handlePointerDown, { capture: true });
  }, [supportsHover]);

  const markPending = useCallback((ids) => {
    if (!ids.length) return;
    setPendingNodeIds((prev) => {
      const updated = new Set(prev);
      ids.forEach((id) => updated.add(id));
      return updated;
    });
  }, []);

  const clearPending = useCallback((ids) => {
    if (!ids.length) return;
    setPendingNodeIds((prev) => {
      const updated = new Set(prev);
      ids.forEach((id) => updated.delete(id));
      return updated;
    });
  }, []);

  const handleAdjustPoints = useCallback(
    async (targetNode, delta) => {
      if (!targetNode?.data || !delta) {
        return;
      }

      const ids = targetNode
        .ancestors()
        .map((node) => node.data.id)
        .filter((id) => id && id !== "birds-eye-root");

      const uniqueIds = Array.from(new Set(ids));

      if (!uniqueIds.length) {
        return;
      }

      markPending(uniqueIds);
      try {
        await Promise.all(
          uniqueIds.map((id) =>
            updateDoc(doc(db, "progress", id), {
              points: increment(delta),
            })
          )
        );

        if (targetNode.data.id) {
          await updateActivity(targetNode.data, delta);
        }
      } catch (error) {
        console.error("BirdsEyePage: failed to update points", error);
        setErrorMessage("Unable to update points right now. Please try again.");
      } finally {
        clearPending(uniqueIds);
      }
    },
    [clearPending, markPending]
  );

  const renderArc = useCallback(
    (node) => {
      if (!node || node.depth === 0) {
        return null;
      }

      const angularSpan = Math.max(node.x1 - node.x0, 0);
      const radialSpan = Math.max(node.y1 - node.y0, 0);

      if (angularSpan <= 0 || radialSpan <= 0) {
        return null;
      }

      const nodeKey =
        node.data.id ||
        `${node.data.title || "node"}-${node.depth}-${Math.round(node.x0 * 1000)}-${Math.round(
          node.y0 * 1000
        )}`;

      const value = normalizePoints(node.data.points);
      const parentNode = node.parent;
      const siblingScale = parentNode ? siblingColorScales.get(parentNode) : null;
      const fill = siblingScale ? siblingScale(value) : fallbackColorScale(value);

      const hasChildren = Boolean(node.children && node.children.length);

      const paddedInnerRadius = Math.max(node.y0, 0);
      const paddedOuterRadius = Math.max(node.y1, paddedInnerRadius + 0.6);

      const baseAnglePad = angularSpan * (hasChildren ? 0.006 : 0.0035);
      const rawAnglePadding = Math.min(baseAnglePad, 0.0007);
      const maxSafeAnglePadding = Math.max(angularSpan * 0.12 - 1e-4, 0);
      const anglePadding = Math.min(rawAnglePadding, maxSafeAnglePadding);

      const startAngle = node.x0 + anglePadding;
      const endAngle = node.x1 - anglePadding;

      if (endAngle <= startAngle) {
        return null;
      }

      const localArc = arc()
        .startAngle(() => startAngle)
        .endAngle(() => endAngle)
        .innerRadius(() => paddedInnerRadius)
        .outerRadius(() => paddedOuterRadius)
        .cornerRadius(0);

      const pathData = localArc(node);
      if (!pathData) {
        return null;
      }

      const [centroidX, centroidY] = localArc.centroid(node);
      const midAngle = (startAngle + endAngle) / 2;

      const fontSize = getFontSizeForDepth(node.depth);
      const minAngularSpanForLabel =
        MIN_LABEL_ARC_SPAN * (node.depth <= 1 ? 0.45 : node.depth === 2 ? 0.7 : 1.05);
      const minRadialSpanForLabel =
        node.depth <= 1
          ? MIN_LABEL_RADIAL_SPAN * 0.5
          : node.depth === 2
          ? MIN_LABEL_RADIAL_SPAN * 0.7
          : MIN_LABEL_RADIAL_SPAN * 0.95;

      const showLabel =
        Boolean(node.data.title) &&
        radialSpan >= minRadialSpanForLabel &&
        angularSpan >= minAngularSpanForLabel;

      const textColor =
        theme.palette.mode === "dark" ? alpha("#f8fafc", 0.92) : getTextColorForBackground(fill);

      const isSelected = Boolean(node.data.id && selectedNodeId === node.data.id);

      const strokeColor = isSelected ? theme.palette.primary.main : "transparent";
      const rawAngleDeg = (midAngle * 180) / Math.PI - 90;
      const shouldFlip = rawAngleDeg > 90 || rawAngleDeg < -90;
      const labelRotation = shouldFlip ? rawAngleDeg + 180 : rawAngleDeg;
      const textAnchor = "middle";

      return (
        <g
          key={nodeKey}
          role={node.data.id ? "button" : "presentation"}
          tabIndex={node.data.id ? 0 : -1}
          aria-label={node.data.title || "Untitled"}
          aria-pressed={isSelected}
          onPointerDown={(event) => {
            if (event.pointerType === "mouse" && event.button !== 0) {
              return;
            }
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.stopPropagation();
            if (!node.data.id) {
              return;
            }
            setSelectedNodeId((prev) => (prev === node.data.id ? null : node.data.id));
          }}
          onKeyUp={(event) => {
            if (!node.data.id) return;
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setSelectedNodeId((prev) => (prev === node.data.id ? null : node.data.id));
            }
          }}
          style={{ cursor: node.data.id ? "pointer" : "default" }}
        >
          <path
            d={pathData}
            fill={fill}
            stroke={strokeColor}
            strokeWidth={isSelected ? 2.6 : 0}
            vectorEffect="non-scaling-stroke"
            style={{
              transition: "stroke 160ms ease, stroke-width 160ms ease, fill 200ms ease",
            }}
          />
          {showLabel && (
            <text
              x={0}
              y={0}
              dx="0"
              dy="0.28em"
              fill={textColor}
              fontSize={fontSize}
              fontWeight={node.depth <= 1 ? 700 : 600}
              style={{
                pointerEvents: "none",
                textTransform: node.depth === 1 ? "uppercase" : "none",
                letterSpacing: node.depth <= 1 ? "0.24px" : "0.05px",
                paintOrder: "stroke fill",
              }}
              stroke={
                theme.palette.mode === "dark"
                  ? "rgba(7, 12, 19, 0.82)"
                  : "rgba(255,255,255,0.94)"
              }
              strokeWidth={theme.palette.mode === "dark" ? 0.36 : 0.25}
              textAnchor={textAnchor}
              dominantBaseline="middle"
              alignmentBaseline="middle"
              transform={`translate(${centroidX}, ${centroidY}) rotate(${labelRotation})`}
            >
              {node.data.title || "Untitled"}
            </text>
          )}
        </g>
      );
    },
    [
      fallbackColorScale,
      selectedNodeId,
      siblingColorScales,
      theme.palette.mode,
      theme.palette.primary.main,
      setSelectedNodeId,
    ]
  );

  const selectedNode = useMemo(() => {
    if (!sunburstRoot || !selectedNodeId) return null;
    return sunburstRoot
      .descendants()
      .find((node) => node.data.id === selectedNodeId);
  }, [sunburstRoot, selectedNodeId]);

  const sunburstNodes = useMemo(() => {
    if (!sunburstRoot) {
      return [];
    }
    return sunburstRoot.descendants().filter((node) => node.depth > 0);
  }, [sunburstRoot]);

  const selectedNodeInfo = useMemo(() => {
    if (!selectedNode) return null;
    const pathTitles = selectedNode
      .ancestors()
      .reverse()
      .map((node) => node.data.title)
      .filter(Boolean);

    const { data } = selectedNode;
    const normalizedPoints = normalizePoints(data.points);

    const hasDailyPoints =
      data.dailyPoints !== undefined && data.dailyPoints !== null && data.dailyPoints !== "";
    let dailyPointsValue = null;
    let dailyPointsLabel = "—";

    if (hasDailyPoints) {
      const numericDaily = Number(data.dailyPoints);
      if (!Number.isNaN(numericDaily)) {
        dailyPointsValue = normalizePoints(data.dailyPoints);
        dailyPointsLabel = dailyPointsValue;
      } else {
        dailyPointsLabel = data.dailyPoints;
      }
    }

    return {
      id: data.id,
      title: data.title || "Untitled",
      image: data.image,
      unit: data.unit,
      points: normalizedPoints,
      rawDailyPoints: data.dailyPoints,
      dailyPointsValue,
      dailyPointsLabel,
      pathTitles,
      node: selectedNode,
    };
  }, [selectedNode]);

  const selectedNodePending = Boolean(
    selectedNodeInfo?.node?.data?.id && pendingNodeIds.has(selectedNodeInfo.node.data.id)
  );
  const selectedNodeHasId = Boolean(selectedNodeInfo?.node?.data?.id);
  const adjustButtonsDisabled = !selectedNodeHasId || selectedNodePending;
  const formattedTotalPoints = selectedNodeInfo && Number.isFinite(selectedNodeInfo.points)
    ? selectedNodeInfo.points.toLocaleString()
    : selectedNodeInfo?.points ?? "0";
  const unitLabel = selectedNodeInfo?.unit && String(selectedNodeInfo.unit).trim()
    ? selectedNodeInfo.unit
    : "Not set";

  const detailPanel = selectedNodeInfo ? (
    <Stack
      direction={isLandscapeLayout ? "column" : { xs: "column", md: "row" }}
      spacing={isLandscapeLayout ? 2.5 : 3}
      alignItems={isLandscapeLayout ? "stretch" : { xs: "flex-start", md: "center" }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: isLandscapeLayout ? "100%" : { md: 160 },
          height: isLandscapeLayout ? 220 : { xs: 200, md: 160 },
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
          boxShadow: theme.shadows[3],
          overflow: "hidden",
          backgroundColor: selectedNodeInfo.image
            ? "transparent"
            : alpha(
                theme.palette.background.default,
                theme.palette.mode === "light" ? 0.6 : 0.4
              ),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: isLandscapeLayout ? 0 : 2,
          textAlign: "center",
        }}
      >
        {selectedNodeInfo.image ? (
          <Box
            component="img"
            src={selectedNodeInfo.image}
            alt={`${selectedNodeInfo.title} visual`}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <Typography
            variant="body2"
            sx={{
              color:
                theme.palette.mode === "light"
                  ? alpha(theme.palette.text.secondary, 0.85)
                  : alpha(theme.palette.common.white, 0.7),
            }}
          >
            No image on record
          </Typography>
        )}
      </Box>
      <Box sx={{ flexGrow: 1, width: "100%" }}>
        <Stack direction="row" spacing={1.2} alignItems="center" flexWrap="wrap">
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {selectedNodeInfo.title}
          </Typography>
          <Chip
            icon={<InfoOutlinedIcon sx={{ fontSize: 18 }} />}
            label={selectedNodeInfo.pathTitles.join(" › ")}
            size="small"
            sx={{
              maxWidth: "100%",
              fontWeight: 500,
              backgroundColor: alpha(theme.palette.info.main, 0.14),
            }}
          />
        </Stack>

        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            {formattedTotalPoints}
          </Typography>
          <Chip
            label={`Daily target: ${selectedNodeInfo.dailyPointsLabel}`}
            size="small"
            sx={{
              fontWeight: 500,
              backgroundColor: alpha(theme.palette.warning.main, 0.12),
            }}
          />
          <Chip
            label={`Unit: ${unitLabel}`}
            size="small"
            sx={{
              fontWeight: 500,
              backgroundColor: alpha(theme.palette.info.main, 0.12),
            }}
          />
        </Stack>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2.2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          sx={{ mt: 3 }}
        >
          <Stack direction="row" spacing={1.5}>
            <Tooltip title="Celebrate progress" arrow>
              <span>
                <IconButton
                  size="medium"
                  onClick={() => handleAdjustPoints(selectedNodeInfo.node, 1)}
                  disabled={adjustButtonsDisabled}
                  sx={{
                    color: theme.palette.success.main,
                    backgroundColor: alpha(theme.palette.success.main, 0.16),
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.35)}`,
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.success.main, 0.24),
                    },
                  }}
                >
                  <CheckCircleRoundedIcon fontSize="medium" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Reflect a setback" arrow>
              <span>
                <IconButton
                  size="medium"
                  onClick={() => handleAdjustPoints(selectedNodeInfo.node, -1)}
                  disabled={adjustButtonsDisabled}
                  sx={{
                    color: theme.palette.error.main,
                    backgroundColor: alpha(theme.palette.error.main, 0.16),
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.error.main, 0.35)}`,
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.error.main, 0.24),
                    },
                  }}
                >
                  <CloseRoundedIcon fontSize="medium" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <Typography
            variant="body2"
            sx={{
              color:
                theme.palette.mode === "light"
                  ? alpha(theme.palette.text.secondary, 0.85)
                  : alpha(theme.palette.common.white, 0.75),
              maxWidth: 320,
            }}
          >
            These adjustments update your overall progress and today's log for this item.
          </Typography>
          {selectedNodePending && (
            <Typography
              variant="caption"
              sx={{
                color:
                  theme.palette.mode === "light"
                    ? alpha(theme.palette.text.secondary, 0.75)
                    : alpha(theme.palette.common.white, 0.7),
              }}
            >
              Syncing changes...
            </Typography>
          )}
        </Stack>
      </Box>
    </Stack>
  ) : (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{
        color:
          theme.palette.mode === "light"
            ? alpha(theme.palette.text.secondary, 0.8)
            : alpha(theme.palette.common.white, 0.75),
      }}
    >
      <InfoOutlinedIcon />
      <Typography variant="body2">
        Click a segment in the sunburst to view its details and update your progress here.
      </Typography>
    </Stack>
  );

  const hasNodes = Boolean(sunburstRoot && sunburstRoot.children && sunburstRoot.children.length > 0);

  useEffect(() => {
    if (!selectedNodeId || !sunburstRoot) {
      return;
    }
    const exists = sunburstRoot
      .descendants()
      .some((node) => node.data.id === selectedNodeId);
    if (!exists) {
      setSelectedNodeId(null);
    }
  }, [selectedNodeId, sunburstRoot]);

  useEffect(() => {
    if (!hasNodes) {
      const identity = zoomIdentity;
      transformRef.current = identity;
      setOffset({ x: 0, y: 0 });
      setZoom(1);
      layoutInitializedRef.current = false;
      if (zoomBehaviorRef.current && containerRef.current) {
        select(containerRef.current).call(zoomBehaviorRef.current.transform, identity);
      }
    }
  }, [hasNodes]);

  useEffect(() => {
    if (!hasNodes || !sunburstRoot) {
      return;
    }
    if (!containerRef.current || !zoomBehaviorRef.current) {
      return;
    }
    if (layoutInitializedRef.current) {
      return;
    }

    const centerX = viewportSize.width / 2;
    const centerY = viewportSize.height / 2;
    const initialTransform = zoomIdentity.translate(centerX, centerY);

    layoutInitializedRef.current = true;
    transformRef.current = initialTransform;
    setOffset({ x: initialTransform.x, y: initialTransform.y });
    setZoom(initialTransform.k);
    select(containerRef.current).call(zoomBehaviorRef.current.transform, initialTransform);
  }, [hasNodes, sunburstRoot, viewportSize.height, viewportSize.width]);

  useEffect(() => {
    layoutInitializedRef.current = false;
  }, [viewportSize.height, viewportSize.width]);

  return (
    <Box
      sx={{
        minHeight: "100%",
        py: { xs: 2.5, md: 4 },
        px: { xs: 2, md: 4 },
        background: pageBackground,
        transition: "background 320ms ease",
      }}
    >
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: { xs: 3, md: 4 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            ...panelSurface,
            px: { xs: 2.75, md: 3.75 },
            py: { xs: 3, md: 4 },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 2.5, md: 3.5 },
            alignItems: { xs: "flex-start", md: "center" },
            background:
              theme.palette.mode === "light"
                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.18)} 0%, ${alpha(accentTone, 0.12)} 38%, ${alpha(theme.palette.background.paper, 0.96)} 100%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.42)} 0%, ${alpha(accentTone, 0.3)} 40%, ${alpha(theme.palette.background.paper, 0.78)} 100%)`,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              background:
                theme.palette.mode === "light"
                  ? `radial-gradient(circle at 18% 25%, ${alpha(theme.palette.common.white, 0.5)} 0%, transparent 55%)`
                  : `radial-gradient(circle at 18% 25%, ${alpha(theme.palette.primary.light ?? theme.palette.primary.main, 0.28)} 0%, transparent 60%)`,
              opacity: 0.9,
            }}
          />
          <Box
            sx={{
              position: "relative",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <Typography variant="overline" sx={{ letterSpacing: 1.6, opacity: 0.72 }}>
              Progress Universe
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Bird's-Eye Overview
            </Typography>
            <Typography
              variant="body1"
              sx={{
                maxWidth: 660,
                color:
                  theme.palette.mode === "light"
                    ? alpha(theme.palette.text.primary, 0.78)
                    : alpha(theme.palette.common.white, 0.9),
              }}
            >
              Survey every outcome and habit at once. Use ⌘/Ctrl + scroll or pinch to zoom, then drag to navigate through your personal progress map.
            </Typography>
          </Box>
          <Stack
            sx={{
              position: "relative",
              flexShrink: 0,
              gap: 1,
              minWidth: { md: 220 },
            }}
          >
            <Chip
              label="⌘ / Ctrl + Scroll to zoom"
              size="small"
              sx={{
                fontWeight: 600,
                letterSpacing: 0.3,
                backgroundColor: alpha(accentTone, 0.16),
                color:
                  theme.palette.mode === "light"
                    ? theme.palette.primary.main
                    : alpha(theme.palette.common.white, 0.92),
              }}
            />
            <Chip
              label="Drag chart to pan"
              size="small"
              sx={{
                fontWeight: 600,
                letterSpacing: 0.3,
                backgroundColor: alpha(theme.palette.primary.main, 0.16),
                color:
                  theme.palette.mode === "light"
                    ? theme.palette.primary.main
                    : alpha(theme.palette.common.white, 0.9),
              }}
            />
          </Stack>
        </Paper>

        {errorMessage && (
          <Alert
            severity="error"
            sx={{
              mt: -1,
              mx: "auto",
              width: "100%",
              maxWidth: 720,
              boxShadow: theme.shadows[4],
              borderRadius: 2,
            }}
            onClose={() => setErrorMessage("")}
          >
            {errorMessage}
          </Alert>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: isLandscapeLayout ? "row" : "column",
            alignItems: "stretch",
            gap: { xs: 2.5, md: 3 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              ...panelSurface,
              px: 0,
              py: 0,
              minHeight: 340,
              height: { xs: 480, md: 620 },
              flex: isLandscapeLayout ? "1 1 60%" : "1 1 auto",
              minWidth: 0,
            }}
          >
            <Box
              ref={containerRef}
              sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                cursor: isPanning ? "grabbing" : "grab",
                touchAction: "none",
                overflow: "hidden",
                borderRadius: 3,
              }}
            >
              {loading && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 3,
                    backdropFilter: "blur(4px)",
                    backgroundColor: alpha(theme.palette.background.default, 0.2),
                  }}
                >
                  <CircularProgress thickness={5} />
                </Box>
              )}

              {!loading && hasNodes && sunburstRoot && (
                <svg
                  width="100%"
                  height="100%"
                  viewBox={`0 0 ${viewportSize.width} ${viewportSize.height}`}
                  style={{
                    position: "absolute",
                    inset: 0,
                  }}
                >
                  <g transform={`translate(${offset.x}, ${offset.y}) scale(${zoom})`}>
                    {sunburstNodes.map((node) => renderArc(node))}
                  </g>
                </svg>
              )}

              {!loading && (!sunburstRoot || !hasNodes) && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color:
                      theme.palette.mode === "light"
                        ? alpha(theme.palette.text.primary, 0.7)
                        : alpha(theme.palette.common.white, 0.75),
                    textAlign: "center",
                    px: 3,
                  }}
                >
                  <Typography variant="subtitle1">
                    Add an outcome or habit in your dashboard to see it represented here.
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              ...panelSurface,
              px: { xs: 2.5, md: 3 },
              py: { xs: 2.5, md: 3 },
              flex: isLandscapeLayout ? "0 0 360px" : "1 1 auto",
              maxWidth: isLandscapeLayout ? 420 : "100%",
              minWidth: isLandscapeLayout ? 320 : "100%",
            }}
          >
            {detailPanel}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default BirdsEyePage;
